import {DestroyRef, Injectable, computed, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {GrantChangeNotifierService} from '../../_modules/company/access/grant-change-notifier.service';
import {AuthenticationService} from '../authentication.service';
import {ContextOptions, SelectableContext, contextTitle, isCurrentContext} from './context.models';

/**
 * The one copy of "where may this account work" the whole app reads — the answer to
 * `GET /auth/context/available`, fetched once per session and kept current.
 *
 * ## Why the state does not live in the component (#602)
 *
 * `ContextSwitcherComponent` used to own this outright: it fetched in its own `ngOnInit`, held the result in
 * its own signal, and subscribed to {@link GrantChangeNotifierService} itself. That was correct while there was
 * exactly one instance of it in the whole application (`app.component.html`). #602 breaks that assumption — the
 * switcher now renders as a floating pill at `sm+` **and** as a chip inside two different top bars below `sm`,
 * so the same control is declared in three places. Left in the component, every declaration would open its own
 * request for the same list, and two of them could briefly disagree about it.
 *
 * So the fetch, the list and the refresh subscription moved here, and the component became a pure renderer over
 * this store. {@link ensureLoaded} is idempotent, which is what makes "declared three times, fetched once" true
 * rather than merely intended. Only one of those declarations is ever *rendered* at a time (the breakpoint
 * decides — see the component), but all three are constructed, so idempotency is the load-bearing part.
 *
 * Nothing here is shard- or company-scoped state: entering a context replaces the whole document
 * (`window.open('/', '_self')`), so this store never has to be invalidated across a context change.
 */
@Injectable({providedIn: 'root'})
export class ContextOptionsStore {
  private readonly auth = inject(AuthenticationService);
  private readonly grantChanges = inject(GrantChangeNotifierService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly options = signal<ContextOptions | null>(null);

  /** Whether {@link ensureLoaded} has already run — the idempotency this store exists for. */
  private started = false;

  /** Every context this account may enter, or an empty list while the answer is unknown or failed. */
  readonly contexts = computed<SelectableContext[]>(() => this.options()?.contexts ?? []);

  /** Whether the backend says this list is NOT the account's real list (a platform owner — no search endpoint). */
  readonly requiresSearch = computed(() => this.options()?.requiresSearch ?? false);

  /**
   * The name of the context this session is in right now, or `null` when the session is in none of the listed
   * ones (or the list is not loaded yet).
   *
   * Lives here rather than in the switcher because the employee panel's drawer head shows the same label under
   * the person's name (#602) without hosting a switcher of its own.
   */
  readonly currentLabel = computed<string | null>(() => {
    const active = this.contexts().find(context => isCurrentContext(context, this.auth.userValue));
    return active ? contextTitle(active) : null;
  });

  /**
   * Fetches the list once per application lifetime and keeps it current afterwards.
   *
   * Safe to call from every host that renders or reads the switcher, on every construction — the second and
   * later calls do nothing at all.
   */
  ensureLoaded(): void {
    if (this.started) return;
    this.started = true;

    // A password session has no `ext_id`, so the endpoint would answer 403 for certain. Not asking is both one
    // request fewer on every page load and one fewer red line in the console for the majority of sign-ins.
    if (!this.auth.canSelectContext) return;

    this.fetch(true);

    // Refreshes the list after a grant is created or revoked ANYWHERE in the app — a my-grants-panel revoke, or
    // a self-grant issued from a chooser mounted on the same page (see `GrantChangeNotifierService`). This is
    // the fix for the staleness the switcher used to have: it kept offering a tenant that had just been taken
    // away until a full page reload. `reload()` never calls `notifyChanged()`, so this cannot cascade.
    this.grantChanges.changed$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.reload());
  }

  /**
   * Re-reads the list — after a self-activation, so the tenant just unlocked appears without a page reload.
   *
   * Deliberately silent on failure, unlike the first fetch: the grant itself succeeded and the panel below says
   * so, a red line here would report the wrong failure, and the list refreshes on the next page load anyway.
   */
  reload(): void {
    this.fetch(false);
  }

  /**
   * @param resetOnError whether a failed request should clear the list. True only for the very first fetch,
   *   where staying invisible beats rendering a broken card in the corner of every page; a failed *refresh*
   *   keeps the last good answer rather than making the control vanish under somebody's cursor.
   */
  private fetch(resetOnError: boolean): void {
    this.auth.availableContexts().subscribe({
      next: options => this.options.set(options),
      error: () => {
        if (resetOnError) this.options.set(null);
      }
    });
  }
}
